import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Alert, Button, Form, Spinner } from "react-bootstrap";
import Swal from "sweetalert2";
import api from "@/api/api.js";
import i18n from "@/i18n.js";
import { TAX_REGIMES } from "@/config/satCatalogs.js";
import CompanyFields from "@/pages/shared/sections/CompanyFields.jsx";
import FiscalDocumentUpload from "@/pages/shared/sections/FiscalDocumentUpload.jsx";
import SignatureUpload from "@/pages/shared/sections/SignatureUpload.jsx";
import SignatureStatus from "@/pages/shared/sections/SignatureStatus.jsx";
import CertificateTaxStatus from "@/pages/shared/sections/CertificateTaxStatus.jsx";

const emptyForm = {
  type: "fisica",
  businessName: "",
  name: "",
  taxIdentification: "",
  taxRegime: "",
  street: "",
  exteriorNumber: "",
  interiorNumber: "",
  suburb: "",
  municipality: "",
  state: "",
  postalCode: "",
  phone: "",
  email: "",
};

const FIELDS = Object.keys(emptyForm);

const CompanyEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [signatureRefresh, setSignatureRefresh] = useState(0);
  const [certificateRefresh, setCertificateRefresh] = useState(0);
  // Una constancia y una e.firma vigentes por empresa: cargada la suya, el
  // usuario ve la ficha del documento y el reemplazo pasa a ser del contador.
  const [hasCertificate, setHasCertificate] = useState(null);
  const [hasSignature, setHasSignature] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const mirar = (documento, guardar) =>
      api
        .get(`/companies/${id}/${documento}/latest`)
        .then(() => !cancelled && guardar(true))
        .catch(() => !cancelled && guardar(false));

    mirar("certificateTax", setHasCertificate);
    mirar("digitalSignature", setHasSignature);

    return () => {
      cancelled = true;
    };
  }, [id, certificateRefresh, signatureRefresh]);

  useEffect(() => {
    api
      .get(`/companies/${id}`)
      .then(({ data }) => {
        const next = { ...emptyForm };
        FIELDS.forEach((key) => {
          if (data[key] !== undefined && data[key] !== null) next[key] = data[key];
        });
        setForm(next);
      })
      .catch(() => setError(i18n.t("ui.accountLoadError")))
      .finally(() => setLoading(false));
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const regimes = TAX_REGIMES.filter(
    (r) => r.type === form.type || r.type === "ambos",
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;
    setError(null);
    setSaving(true);
    try {
      await api.put(`/companies/${id}`, form);
      Swal.fire({
        icon: "success",
        title: i18n.t("ui.accountUpdated"),
        timer: 1800,
        showConfirmButton: false,
      });
    } catch (err) {
      const violations = err.response?.data?.violations;
      setError(
        violations?.length
          ? violations.map((v) => v.message).join(" ")
          : err.response?.data?.["hydra:description"] ||
              "No se pudieron guardar los cambios.",
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="col">
        <div className="card">
          <div className="card-body text-center py-5">
            <Spinner animation="border" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="col">
        <div className="card">
          <div className="card-body">
            <div className="border-bottom pb-2 mb-3 d-flex justify-content-between align-items-center">
              <div>
                <h5 className="mb-0">{i18n.t("ui.editAccount")}</h5>
                <small className="text-muted">
                  {i18n.t("ui.editAccountHint")}
                </small>
              </div>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => navigate(`/companies/${id}`)}
              >
                <i className="bi bi-arrow-left me-1" />
                Volver
              </Button>
            </div>

            {error && <Alert variant="danger">{error}</Alert>}

            <Form onSubmit={handleSubmit}>
              <CompanyFields
                form={form}
                handleChange={handleChange}
                regimes={regimes}
              />

              <div className="d-flex align-items-center gap-2 mt-3 mb-2">
                <Button type="submit" variant="primary" disabled={saving}>
                  {saving ? "Guardando..." : "Guardar cambios"}
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => navigate(`/companies/${id}`)}
                >
                  Cancelar
                </Button>
              </div>
            </Form>
          </div>
        </div>
      </div>

      <div className="col">
        <div className="card">
          <div className="card-body">
            <div className="border-bottom pb-2 mb-3">
              <h5 className="mb-0">Documentos</h5>
              <small className="text-muted">
                Tu constancia y tu firma electrónica se cargan una vez.
                Actualizarlas es tarea de tu contador: pídeselo y él sube la
                nueva sin que pierdas la anterior.
              </small>
            </div>

            <h6 className="text-primary fw-bold mb-2">
              Constancia de situación fiscal
            </h6>
            {hasCertificate ? (
              <div className="mb-3">
                <CertificateTaxStatus
                  companyId={id}
                  refreshKey={certificateRefresh}
                />
              </div>
            ) : (
              <FiscalDocumentUpload
                companyId={id}
                onUploadSuccess={() => {
                  setCertificateRefresh((v) => v + 1);
                  Swal.fire({
                    icon: "success",
                    title: "Constancia cargada",
                    timer: 1800,
                    showConfirmButton: false,
                  });
                }}
              />
            )}

            <h6 className="text-primary fw-bold mb-2 mt-3">
              FIEL / Certificado (.cer, .key y contraseña)
            </h6>
            <div className="mb-3">
              <SignatureStatus companyId={id} refreshKey={signatureRefresh} />
            </div>
            {!hasSignature && (
              <SignatureUpload
                companyId={id}
                onUploadSuccess={() => setSignatureRefresh((v) => v + 1)}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default CompanyEdit;
